import React, { useContext, useEffect } from 'react'
import { css } from '@emotion/core'
import useBrowserContextCommunication from 'react-window-communication-hook'
import { useTranslation } from 'react-i18next'
import { signout } from '@/apis/auth0'
import { Link } from 'react-router-dom'
import { AccountContext } from '@/contexts/account'
import { HeaderListContainer } from './header-list-container'
import { setEmployeeCurrentHotel } from '@/apis/aipass'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { useCurrentHotel } from '@/hooks/use-current-hotel'
import { useRestoreLanguage } from '@/hooks/use-restore-language'
import { CleanerLanguages } from '@/constants/cleaning-manager'

// 通常アカウントの施設選択 UI と同じ幅
const HOTEL_SWITCHER_WIDTH = 300

type HeaderProps = {
  title: string
  reload?: () => void
}

export const Header: React.FC<HeaderProps> = ({ title, reload }) => {
  const [communicationState, postMessage] = useBrowserContextCommunication('')
  const { t, i18n } = useTranslation()
  const { account, employeeHotels } = useContext<any>(AccountContext)
  const { errorHandler } = useErrorHandler()
  const { hotelName, logoImageSrc } = useCurrentHotel()
  useRestoreLanguage(CleanerLanguages)

  const logout = async () => {
    postMessage('logout')
  }

  const onClickHotel = async (hotel: any) => {
    try {
      await setEmployeeCurrentHotel(hotel.id)
      window.location.replace('/')
    } catch (error) {
      errorHandler(error)
    }
  }

  useEffect(() => {
    // Log out the management screen of all tabs when logging out (to avoid duplicate logins)
    if (communicationState.lastMessage === 'logout') {
      signout()
    }
  }, [postMessage])

  return (
    <div css={headerContainerStyle}>
      <div css={hearerLeftPartStyle}>
        <div css={sideMenu}>
          <img src={logoImageSrc} css={hotelLogoStyle} alt="" />
          <p css={hotelNameStyle}>{hotelName}</p>
          <img src={require(`@/static/images/self-checkin-payment/arrow.svg`)} css={languageArrowImageStyle} alt="down-arrow" />

          <div className="list">
            <div className="list__container">
              <div>
                <Link to={'/cleaning/'}>
                  <img src={require(`@/static/images/cleaning_manager/icon_menu_black.svg`)} width={16} />
                  <p style={{ paddingLeft: 8 }}>{t('Cleaning status')}</p>
                </Link>
              </div>

              <div>
                <Link to={'/cleaning/schedule'}>
                  <img src={require(`@/static/images/calendar_icon.svg`)} width={16} />
                  <p style={{ paddingLeft: 8 }}>{t('Schedule')}</p>
                </Link>
              </div>

              {employeeHotels.length > 1 && (
                <HeaderListContainer
                  icon={require(`@/static/images/header/hotel.svg`)}
                  title={t('switch hotel')}
                  childrenWidth={HOTEL_SWITCHER_WIDTH}
                >
                  <div className="list__container">
                    {employeeHotels.map((hotel: any, index: number) => (
                      <div
                        key={hotel.id}
                        onClick={() => onClickHotel(hotel)}
                        style={{ color: hotel.id === account?.hotel?.id ? '#F2A40B' : '#676767' }}
                      >
                        {hotel.name}
                      </div>
                    ))}
                  </div>
                </HeaderListContainer>
              )}

              <HeaderListContainer icon={require(`@/static/images/header/language.svg`)} title="Language">
                <div className="list__container">
                  <div onClick={() => i18n.changeLanguage('ja')} style={{ color: i18n.language === 'ja' ? '#F2A40B' : '#676767' }}>
                    日本語
                  </div>
                  <div onClick={() => i18n.changeLanguage('en')} style={{ color: i18n.language === 'en' ? '#F2A40B' : '#676767' }}>
                    English
                  </div>
                </div>
              </HeaderListContainer>

              <div onClick={() => logout()}>
                <img src={require(`@/static/images/header/icon_logout.svg`)} width={16} />
                <p>{t('Logout')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div css={hearerCenterPartStyle}>
        <h2 css={mainTitleStyle}>{title}</h2>
      </div>

      <div css={hearerRightPartStyle}>
        <div css={noticeLogoWrapperStyle} onClick={() => reload && reload()}>
          <img src={require('@/static/images/reload.svg')} height="20" />
        </div>
      </div>
    </div>
  )
}
const headerContainerStyle = css({
  height: 55,
  boxShadow: '0px 1px 6px rgba(0, 0, 0, 0.10)',
  width: '100%',
  background: '#fff',
  display: 'flex',
  position: 'sticky',
  top: 0,
  zIndex: 100,
})

const hearerLeftPartStyle = css({
  display: 'flex',
  justifyContent: 'start',
  paddingLeft: '24px',
  width: '20%',
  maxWidth: '240px',
  alignItems: 'center',
})
const hearerCenterPartStyle = css({
  display: 'flex',
  justifyContent: 'center',
  flexGrow: 1,
})
const hearerRightPartStyle = css({
  display: 'flex',
  justifyContent: 'end',
  alignItems: 'center',
  paddingRight: '24px',
})

const mainTitleStyle = css({
  fontSize: 16,
  lineHeight: '24px',
  fontWeight: 'bold',
  letterSpacing: '1.6px',
  padding: '18px 24px',
  color: '#272727',
})

const noticeLogoWrapperStyle = css({
  margin: '10px 15px 10px 24px',
  paddingLeft: '30px',
  borderLeft: 'solid lightgray',
  alignSelf: 'center',
})

const languageArrowImageStyle = css({
  height: 18,
})

const hotelLogoStyle = css({
  width: 28,
  height: 28,
  border: '1px solid #CCCCCC',
  borderRadius: '50%',
  flexShrink: 0,
})

const hotelNameStyle = css({
  // 省略の挙動は施設選択 UI に合わせる（実効の幅はヘッダー左領域に収まる範囲）
  maxWidth: 250,
  minWidth: 0,
  paddingLeft: 8,
  fontSize: 14,
  // reset.css の line-height: 1 のままだと、overflow: hidden で文字の下端が切れる
  lineHeight: '20px',
  fontWeight: 'bold',
  color: '#676767',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
})

const sideMenu = css({
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  minWidth: 0,
  cursor: 'pointer',
  '.list': {
    position: 'absolute',
    width: 180,
    top: 51,
    visibility: 'hidden',
    opacity: 0,
    transition: '0.3s',
    boxShadow: '0px 3px 10px #0000003D',
    '.list__container': {
      backgroundColor: 'white',
      '& > div': {
        paddingLeft: 16,
        height: 41,
        borderBottom: '1px solid #F2F2F2',
        fontSize: 14,
        fontWeight: 'bold',
        letterSpacing: '0.7px',
        color: '#676767',
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        ':last-child': { borderBottom: 'none' },
        '> p': { paddingLeft: 8 },
      },
    },
  },
  a: {
    display: 'flex',
    color: '#676767',
    ':hover': { color: '#F2A40B' },
  },
  ':hover': {
    '.list': {
      // display: 'block',
      visibility: 'visible',
      opacity: 1,
      transition: '0s',
    },
  },
})
